import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, FileText, Wrench, PackageCheck, Car, ArrowRight, ChevronRight } from "lucide-react";
import { api, STATUS_META, normalizeStatus } from "@/lib/api";
import { Stepper } from "@/components/Stepper";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const m = STATUS_META[status] || STATUS_META.en_attente;
  return <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${m.color}`}>{t(`status.${normalizeStatus(status)}`)}</span>;
}

function useTimeAgo() {
  const { t } = useTranslation();
  return (iso) => {
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days <= 0) return t("home.today");
    if (days === 1) return t("home.yesterday");
    if (days < 30) return t("home.daysAgo", { count: days });
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  };
}

export default function ClientHome() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const timeAgo = useTimeAgo();
  const [quotes, setQuotes] = useState(null);

  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "agent")) { navigate("/app/admin"); return; }
    api.get("/quotes").then(({ data }) => setQuotes(data)).catch(() => setQuotes([]));
  }, [user, navigate]);

  const active = quotes?.filter((q) => !["recuperee", "termine", "annulee", "annule"].includes(q.status)) || [];
  const stats = {
    total: quotes?.length || 0,
    preparing: quotes?.filter((q) => q.status === "en_preparation").length || 0,
    ready: quotes?.filter((q) => normalizeStatus(q.status) === "prete").length || 0,
  };

  const greeting = new Date().getHours() < 18 ? t("home.hiDay") : t("home.hiEve");

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-blue-600">{greeting},</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">{user?.name?.split(" ")[0]} 👋</h1>
        </div>
        <Link to="/app/nouvelle-demande" data-testid="new-request-cta">
          <Button className="bg-blue-600 hover:bg-blue-700"><Plus className="mr-1.5 h-4 w-4" /> {t("home.newRequest")}</Button>
        </Link>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {[
          { label: t("home.total"), value: stats.total, icon: FileText, c: "text-blue-600 bg-blue-50" },
          { label: t("home.preparing"), value: stats.preparing, icon: Wrench, c: "text-amber-600 bg-amber-50" },
          { label: t("home.ready"), value: stats.ready, icon: PackageCheck, c: "text-violet-600 bg-violet-50" },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <span className={`grid h-10 w-10 place-items-center rounded-lg ${s.c}`}><s.icon className="h-5 w-5" /></span>
              <span className="font-display text-3xl font-bold text-slate-900">{s.value}</span>
            </div>
            <p className="mt-3 text-sm text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Suivi des demandes */}
      <div className="mt-10 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/60 px-6 py-4">
          <div>
            <h2 className="font-display text-lg font-semibold">{t("home.trackTitle")}</h2>
            <p className="text-xs text-slate-500">{t("home.trackSub")}</p>
          </div>
          <Link to="/app/nouvelle-demande">
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700"><Plus className="mr-1 h-4 w-4" /> {t("home.requestBtn")}</Button>
          </Link>
        </div>

        {quotes === null ? (
          <div className="py-16 text-center text-slate-400">{t("common.loading")}</div>
        ) : quotes.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-blue-50 text-blue-600"><Car className="h-7 w-7" /></span>
            <p className="mt-4 font-medium text-slate-700">{t("home.emptyTitle")}</p>
            <p className="mt-1 text-sm text-slate-500">{t("home.emptySub")}</p>
            <Link to="/app/nouvelle-demande"><Button className="mt-5 bg-blue-600 hover:bg-blue-700">{t("home.createReq")}</Button></Link>
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="quotes-list">
            {quotes.map((q) => (
              <div key={q.id} className="px-5 py-5 transition-colors hover:bg-slate-50/70 sm:px-6" data-testid={`quote-row-${q.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3.5">
                    <span className="mt-0.5 grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-blue-50 text-blue-600">
                      <Car className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-display font-semibold text-slate-900">
                        {q.vehicle?.brand || t("home.vehicle")} {q.vehicle?.model || ""}
                      </p>
                      {q.ref && <p className="mt-0.5 font-mono text-xs font-semibold text-slate-400" data-testid={`row-ref-${q.id}`}>N° {q.ref}</p>}
                      <p className="mt-0.5 line-clamp-1 max-w-md text-sm text-slate-500">{q.parts_description}</p>
                      <p className="mt-1 text-xs text-slate-400">{t("home.updated")} {timeAgo(q.updated_at || q.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {q.quote && <span className="font-display text-lg font-bold text-blue-600">{q.quote.price.toFixed(2)} €</span>}
                    <StatusBadge status={q.status} />
                    <Link to={`/app/demande/${q.id}`} className="hidden items-center gap-1 text-sm font-medium text-blue-600 hover:underline sm:flex" data-testid={`quote-detail-${q.id}`}>
                      {t("home.details")} <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>

                <div className="mt-5 pl-0 sm:pl-[58px]">
                  <Stepper status={q.status} />
                </div>

                <Link to={`/app/demande/${q.id}`} className="mt-4 flex items-center gap-1 text-sm font-medium text-blue-600 sm:hidden">
                  {t("home.details")} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
